import * as React from 'react'
import BackendContext from './backend-context'
import * as firebase from 'firebase/app'
import { getFirebase } from './get-firebase-instance'
import JobOffering from '../../types/jobOffering'
import { addDoc, arrayRemove, arrayUnion, collection, deleteDoc, doc, DocumentData, getFirestore, onSnapshot, QueryDocumentSnapshot, setDoc, updateDoc, writeBatch } from 'firebase/firestore'
import { listAll, getStorage, ref, uploadBytes, deleteObject, getBlob } from 'firebase/storage'
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth'

const offeringsCollectionName = "jobOfferings"
const documentsBucketName = "documents"
const datenschutzName = "datenschutzerklaerung.pdf"

const jobOfferFromDoc = (x: QueryDocumentSnapshot<DocumentData>) => ({
    id: x.id,
    name: x.data().name,
    aufgaben: x.data().aufgaben,
    anforderungen: x.data().anforderungen,
    dasErwartenWir: x.data().dasErwartenWir,
    dasBietenWir: x.data().dasBietenWir,
    maxHeightLarge: x.data().maxHeightLarge,
    maxHeightTablet: x.data().maxHeightTablet,
    maxHeightPhone: x.data().maxHeightPhone
})

const emptyPromise: Promise<void> = new Promise(() => { })

interface BackendProviderProps {
    children: React.ReactNode
}

// Client-side only wrapper to prevent hydration issues
const ClientOnlyBackendProvider: React.FC<BackendProviderProps> = ({ children }) => {
    const [isClient, setIsClient] = React.useState(false)

    React.useEffect(() => {
        setIsClient(true)
    }, [])

    // Return a consistent structure during SSR and initial client render
    if (!isClient) {
        return (
            <BackendContext.Provider value={{
                documents: [],
                addDocument: () => emptyPromise,
                deleteDocument: () => emptyPromise,
                downloadDocument: () => emptyPromise.then(() => null),
                jobOfferings: [],
                addJobOffering: () => emptyPromise,
                editJobOffering: () => emptyPromise,
                deleteJobOffering: () => emptyPromise,
                login: () => emptyPromise,
                signedIn: false,
                getDatenschutz: () => emptyPromise.then(() => null)
            }}>
                {children}
            </BackendContext.Provider>
        )
    }

    return <BackendProvider>{children}</BackendProvider>
}

const BackendProvider: React.FC<BackendProviderProps> = ({ children }) => {
    const [firebase, setFirebase] = React.useState<firebase.FirebaseApp | null>(null)
    const [documents, setDocuments] = React.useState<string[]>([])
    const [documentsVersion, setDocumentsVersion] = React.useState(0)
    const [jobOfferings, setJobOfferings] = React.useState<JobOffering[]>([])
    const [signedIn, setSignedIn] = React.useState<boolean>(false)

    React.useEffect(() => {
        if (typeof window !== 'undefined') {
            const firebaseInstance = getFirebase()
            setFirebase(firebaseInstance)
        }
    }, [])

    // Offerings listener
    React.useEffect(() => {
        if (typeof firebase === 'object' && firebase && typeof window !== 'undefined') {
            const db = getFirestore(firebase)
            const unsubscribe = onSnapshot(collection(db, offeringsCollectionName),
                snaphot => {
                    setJobOfferings(snaphot.docs.map(jobOfferFromDoc))
                },
                err => {
                    setJobOfferings([])
                })
            return unsubscribe
        }
    }, [firebase])
    
    // Documents "listener"
    React.useEffect(() => {
        if (typeof firebase === 'object' && firebase && typeof window !== 'undefined') {
            const storage = getStorage(firebase)
            const docsRef = ref(storage, documentsBucketName)
            listAll(docsRef).then(res => {
                setDocuments(res.items.map(x => x.name))
            }).catch(err => {
                setDocuments([])
            })
        }
    }, [firebase, documentsVersion])

    const addDocument = (name: string, file: File) => {
        if (typeof firebase === 'object' && firebase) {
            const storage = getStorage(firebase)
            const docRef = ref(storage, `${documentsBucketName}/${name}`)
            return uploadBytes(docRef, file).then(() => {
                setDocumentsVersion(documentsVersion + 1)
            })
        }
        else return emptyPromise
    }

    const deleteDocument = (name: string) => {
        if (typeof firebase === 'object' && firebase) {
            const storage = getStorage(firebase)
            const docRef = ref(storage, `${documentsBucketName}/${name}`)
            return deleteObject(docRef).then(() => setDocumentsVersion(documentsVersion + 1))
        }
        else return emptyPromise
    }

    const downloadDocument = (name: string) => {
        if (typeof firebase === 'object' && firebase) {
            const storage = getStorage(firebase)
            const docRef = ref(storage, `${documentsBucketName}/${name}`)
            return getBlob(docRef)
        }
        else return emptyPromise.then(_ => null)
    }

    const addJobOffering = (offering: any) => {
        if (typeof firebase === 'object' && firebase) {
            const db = getFirestore(firebase)
            return addDoc(collection(db, offeringsCollectionName), offering).then(() => { })
        }
        else return emptyPromise
    }

    const editJobOffering = (offering: any, removedOffering: any) => {
        if (typeof firebase === 'object' && firebase && typeof offering.id === 'string' && offering.id) {
            const db = getFirestore(firebase)
            const batch = writeBatch(db)

            const docRef = doc(db, offeringsCollectionName, offering.id)
            batch.update(docRef, {
                aufgaben: arrayUnion(...offering.aufgaben),
                anforderungen: arrayUnion(...offering.anforderungen),
                dasErwartenWir: arrayUnion(...offering.dasErwartenWir),
                dasBietenWir: arrayUnion(...offering.dasBietenWir)
            })
            batch.update(docRef, {
                aufgaben: arrayRemove(...removedOffering.aufgaben),
                anforderungen: arrayRemove(...removedOffering.anforderungen),
                dasErwartenWir: arrayRemove(...removedOffering.dasErwartenWir),
                dasBietenWir: arrayRemove(...removedOffering.dasBietenWir)
            })
            batch.update(docRef, {
                maxHeightLarge: offering.maxHeightLarge,
                maxHeightTablet: offering.maxHeightTablet,
                maxHeightPhone: offering.maxHeightPhone
            })
            return batch.commit()
        }
        else return emptyPromise
    }

    const deleteJobOffering = (id: string) => {
        if (typeof firebase === 'object' && firebase) {
            const db = getFirestore(firebase)
            return deleteDoc(doc(db, offeringsCollectionName, id))
        }
        else return emptyPromise
    }

    const login = (email: string, password: string, onAuthStateChanged: (user: any) => void) => {
        if (typeof firebase === 'object' && firebase) {
            const auth = getAuth(firebase)
            return signInWithEmailAndPassword(auth, email, password)
                .then(() => {
                    auth.onAuthStateChanged(user => {
                        setSignedIn(user !== null)
                        onAuthStateChanged(user)
                    })
                })
        }
        else return emptyPromise
    }

    const getDatenschutz = () => {
        if (typeof firebase === 'object' && firebase) {
            const storage = getStorage(firebase)
            const docRef = ref(storage, datenschutzName)
            return getBlob(docRef)
        }
        else return emptyPromise.then(() => null)
    }

    // Use useMemo to create a stable context value to prevent hydration mismatches
    const contextValue = React.useMemo(() => ({
        documents: documents,
        addDocument: addDocument,
        deleteDocument: deleteDocument,
        downloadDocument: downloadDocument,
        jobOfferings: jobOfferings,
        addJobOffering: addJobOffering,
        editJobOffering: editJobOffering,
        deleteJobOffering: deleteJobOffering,
        login: login,
        signedIn: signedIn,
        getDatenschutz: getDatenschutz
    }), [documents, jobOfferings, signedIn, documentsVersion])

    return (<BackendContext.Provider value={contextValue}>
        {children}
    </BackendContext.Provider >)
}

export default ClientOnlyBackendProvider
